import React, { useCallback, useEffect, useState } from 'react';
import { useSocket, persistPlayerSeat } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';
import { translateError, type Room } from '@deadpan/shared';
import { replaceInviteUrlWithHomeIfMatchingRoom } from '../utils/roomInvitePath';

export interface InviteJoinModalProps {
  roomCode: string;
  onClose: () => void;
  onJoined: () => void;
}

export const InviteJoinModal: React.FC<InviteJoinModalProps> = ({ roomCode, onClose, onJoined }) => {
  const { socket, isConnected, error, currentRoom, adoptRoom } = useSocket();
  const t = useTranslations();
  const [playerName, setPlayerName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const relevantError = !currentRoom &&
    (localError ||
      (error &&
      (error.includes('join') ||
        error.includes('Room not found') ||
        error.includes('duplicate') ||
        error.includes('already in the room') ||
        error.includes('full'))
        ? error
        : null));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const joinErrors = [
      'join',
      'Room not found',
      'duplicate',
      'already in the room',
      'full',
      'Player name is required',
    ];
    setLocalError(error && joinErrors.some((j) => error.includes(j)) ? error : null);
    if (currentRoom) setLocalError(null);
  }, [error, currentRoom]);

  const submit = useCallback(async () => {
    if (!socket || !isConnected || !playerName.trim()) return;
    const normalizedRoomCode = roomCode.trim().toUpperCase();
    const normalizedPlayerName = playerName.trim();

    setLocalError(null);
    setIsJoining(true);
    try {
      const joinedRoom = await new Promise<Room | null>((resolve) => {
        socket.emit('client:join-room', normalizedRoomCode, normalizedPlayerName, (room) => {
          resolve(room);
        });
      });

      if (!joinedRoom) {
        return;
      }

      setLocalError(null);
      persistPlayerSeat(joinedRoom, normalizedPlayerName);
      adoptRoom(joinedRoom, normalizedPlayerName);
      replaceInviteUrlWithHomeIfMatchingRoom(joinedRoom.code);
      onJoined();
    } finally {
      setIsJoining(false);
    }
  }, [adoptRoom, isConnected, onJoined, playerName, roomCode, socket]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/35 backdrop-blur-[2px]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-join-title"
        className="relative w-[90%] max-w-md rounded-2xl border border-white/30 bg-white/95 p-4 shadow-2xl sm:p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg text-xl leading-none text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
          aria-label={t.ui.inviteModalClose}
        >
          ×
        </button>
        <h2
          id="invite-join-title"
          className="pr-10 text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent sm:text-3xl"
        >
          {t.ui.joinRoom}
        </h2>
        <p className="mt-2 text-xs text-gray-600 sm:text-sm">{t.ui.joinViaInviteDescription}</p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="inviteJoinPlayerName" className="mb-2 block text-sm font-semibold text-gray-700">
              {t.ui.yourName}
            </label>
            <input
              id="inviteJoinPlayerName"
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder={t.ui.enterYourName}
              className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 outline-none transition focus:border-pink-500 focus:ring-2 focus:ring-pink-500"
              required
              disabled={!isConnected || isJoining}
              maxLength={20}
              autoComplete="name"
              autoFocus
            />
          </div>
          {relevantError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {translateError(relevantError)}
            </div>
          )}
          <button
            type="submit"
            disabled={!isConnected || isJoining || !playerName.trim()}
            className="w-full rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 py-3 px-4 font-semibold text-white shadow-lg transition hover:from-pink-700 hover:to-rose-700 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
          >
            {isJoining ? t.ui.joining : t.ui.inviteJoinConfirm}
          </button>
          {!isConnected && (
            <p className="text-center text-sm text-gray-500">{t.ui.connectingToServer}</p>
          )}
        </form>
      </div>
    </div>
  );
};
